import React, { useState, useRef, useEffect } from "react";
import { Container, Box, Typography, Button, Divider } from "@mui/material";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import { useLocation } from "react-router-dom";
import ProjectGallery from "../components/ProjectGallery";
import projectsData from "../data/projectsData";

const Workspace = () => {
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [activeProject, setActiveProject] = useState(null);
  const location = useLocation();
  const projectRefs = useRef({});

  // Scroll to project if hash is in URL (e.g. /work#palmira)
  useEffect(() => {
    if (location.hash) {
      const projectId = location.hash.replace("#", "").toLowerCase();
      // Small delay to let the page render
      setTimeout(() => {
        const el = projectRefs.current[projectId];
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 100);
    }
  }, [location.hash]);

  const openGallery = (project) => {
    setActiveProject(project);
    setGalleryOpen(true);
  };

  return (
    <Container
      maxWidth={false}
      sx={{ maxWidth: "1000px", mt: 4, mb: 8, px: { xs: "1rem", sm: "1rem", md: "0rem" } }}
    >
      {projectsData.map((proj, idx) => {
        const projectId = proj.header.toLowerCase().replace(/\s+/g, "-");
        const isEven = idx % 2 === 0;

        return (
          <React.Fragment key={idx}>
            <Box
              ref={(el) => (projectRefs.current[projectId] = el)}
              id={projectId}
              sx={{
                mb: 8,
                scrollMarginTop: "80px",
              }}
            >
              {/* Project layout - alternating image/text sides */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: {
                    xs: "column",
                    md: isEven ? "row" : "row-reverse",
                  },
                  gap: { xs: 2, md: 4 },
                  alignItems: "center",
                }}
              >
                {/* Cover Image */}
                <Box
                  sx={{
                    flex: { md: "0 0 55%" },
                    width: "100%",
                    cursor: "pointer",
                  }}
                  onClick={() => openGallery(proj)}
                >
                  <Box
                    component="img"
                    src={proj.images[0]}
                    alt={proj.header}
                    sx={{
                      width: "100%",
                      height: { xs: "280px", sm: "360px", md: "420px" },
                      objectFit: "cover",
                      display: "block",
                      transition: "transform 0.3s ease",
                      "&:hover": {
                        transform: "scale(1.01)",
                      },
                    }}
                    loading="lazy"
                  />
                </Box>

                {/* Text & Button */}
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    px: { xs: 1, md: 0 },
                  }}
                >
                  <Typography
                    variant="h4"
                    fontFamily="Audrey"
                    sx={{ mb: 1.5, letterSpacing: "0.05rem" }}
                  >
                    {proj.header}
                  </Typography>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    fontFamily="Avenir"
                    sx={{ mb: 3, lineHeight: 1.7 }}
                  >
                    {proj.paragraph}
                  </Typography>
                  <Button
                    variant="outlined"
                    onClick={() => openGallery(proj)}
                    endIcon={<PhotoLibraryIcon />}
                    sx={{
                      width: "fit-content",
                      color: "#333",
                      borderColor: "#333",
                      fontFamily: "Avenir",
                      textTransform: "none",
                      px: 3,
                      py: 1,
                      "&:hover": {
                        backgroundColor: "#333",
                        color: "#fff",
                        borderColor: "#333",
                      },
                    }}
                  >
                    View Gallery
                  </Button>
                </Box>
              </Box>

              {/* Thumbnail row */}
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  mt: 2,
                  overflowX: "auto",
                  scrollbarWidth: "none",
                  "-ms-overflow-style": "none",
                  "&::-webkit-scrollbar": { display: "none" },
                  px: { xs: 1, md: 0 },
                }}
              >
                {proj.images.slice(1).map((img, i) => (
                  <Box
                    key={i}
                    component="img"
                    src={img}
                    alt={`${proj.header} ${i + 2}`}
                    sx={{
                      width: { xs: "100px", md: "130px" },
                      height: { xs: "100px", md: "130px" },
                      objectFit: "cover",
                      flexShrink: 0,
                      cursor: "pointer",
                      opacity: 0.85,
                      transition: "all 0.2s ease",
                      "&:hover": {
                        opacity: 1,
                        transform: "scale(1.03)",
                      },
                    }}
                    onClick={() => openGallery(proj)}
                    loading="lazy"
                  />
                ))}
              </Box>
            </Box>
            {idx < projectsData.length - 1 && (
              <Divider sx={{ mb: 8, borderColor: "rgba(0,0,0,0.1)" }} />
            )}
          </React.Fragment>
        );
      })}

      {/* Gallery Modal */}
      {activeProject && (
        <ProjectGallery
          open={galleryOpen}
          onClose={() => setGalleryOpen(false)}
          folder={activeProject.folder}
          title={activeProject.header}
        />
      )}
    </Container>
  );
};

export default Workspace;
